import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import {
  masterOntology,
  MasterOntology,
  Procedure,
  PROCEDURE_CATEGORIES,
  ProcedureCategory,
  Representation,
  Tag,
} from '@examdojo/core/ontology';
import { TreeNode } from '@examdojo/core/tree';
import * as go from 'gojs';
import { VerificationIndicator } from './domain-explorer/domain-node.interface';
import { OntologyDomainExplorerComponent } from './domain-explorer/ontology-domain-explorer.component';
import { GojsOverviewComponent } from './gojs-overview/gojs-overview.component';
import { NodeDetailsComponent } from './node-details/node-details.component';

interface NodeTypeConfig {
  type: string;
  label: string;
  fill: string;
  stroke: string;
  legendColor: string;
  legendBorderColor?: string;
}

interface ProcedureCategoryColors {
  fill: string;
  stroke: string;
  highlight: string;
  text: string;
  edge: string;
}

const PROCEDURE_CATEGORY_COLORS: Record<string, ProcedureCategoryColors> = {
  transformation: {
    fill: '#f5f3ff',
    stroke: '#ddd6fe',
    highlight: '#8b5cf6',
    text: '#5b21b6',
    edge: '#c4b5fd',
  },
  calculation: {
    fill: '#ecfdf5',
    stroke: '#a7f3d0',
    highlight: '#10b981',
    text: '#047857',
    edge: '#6ee7b7',
  },
  operation: {
    fill: '#fff1f1',
    stroke: '#ffc8ca',
    highlight: '#e41e25',
    text: '#9f151a',
    edge: '#ffa1a4',
  },
  derivation: {
    fill: '#eff6ff',
    stroke: '#bfdbfe',
    highlight: '#2563eb',
    text: '#1e40af',
    edge: '#93c5fd',
  },
  classifier: {
    fill: '#fff7ed',
    stroke: '#fed7aa',
    highlight: '#f97316',
    text: '#9a3412',
    edge: '#fdba74',
  },
};

const REPRESENTATION_CATEGORY_GEOMETRIES: Record<string, string> = {
  value: 'M 2 5 L 14 5 M 2 11 L 14 11 M 5.5 2 L 4.5 14 M 11.5 2 L 10.5 14',
  form: 'M 2 14 L 5 14 L 13 6 L 10 3 L 2 11 Z M 9 4 L 12 7',
  expression: 'M 4 13 L 8 3 L 12 13 M 5.5 10 L 10.5 10',
  object:
    'M 5 2 C 3 2 3 3 3 5 L 3 6 C 3 7 2 8 1 8 C 2 8 3 9 3 10 L 3 11 C 3 13 3 14 5 14 M 11 2 C 13 2 13 3 13 5 L 13 6 C 13 7 14 8 15 8 C 14 8 13 9 13 10 L 13 11 C 13 13 13 14 11 14',
  function: 'M 2 8 Q 5 2 8 8 Q 11 14 14 8',
  state: 'M 2 8 L 6 8 M 6 4 L 6 12 M 10 4 L 10 12 M 10 8 L 14 8',
  default: 'M 8 2 A 6 6 0 1 0 8 14 A 6 6 0 1 0 8 2',
};

@Component({
  selector: 'exam-ontology-canvas',
  standalone: true,
  imports: [
    MatSidenavModule,
    MatSlideToggleModule,
    NodeDetailsComponent,
    MatTooltipModule,
    GojsOverviewComponent,
    IconComponent,
    IconButtonComponent,
    OntologyDomainExplorerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ontology-canvas.component.html',
  styleUrls: ['./ontology-canvas.component.scss'],
})
export class OntologyCanvasComponent implements AfterViewInit {
  constructor() {
    this.loadOntology();

    effect(() => {
      const hasTag = this.hasSelectedTag();
      const diagram = this.myDiagram();
      const data = this.ontologyData();
      const deps = this.includeDependencies();

      if (diagram && data) {
        this.renderGraphInternal(diagram, data, hasTag, deps);
      }
    });
  }

  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('myDiagramDiv', { static: false }) myDiagramDiv!: ElementRef;

  readonly ontologyData = signal<MasterOntology | null>(null);
  readonly myDiagram = signal<go.Diagram | null>(null);
  readonly highlightedLegendType = signal<string | null>(null);
  readonly selectedNode = signal<Record<string, unknown> | null>(null);
  readonly sidenavOpened = signal(false);
  readonly detailsPanelWidth = signal(420);
  readonly selectedTags = signal<string[]>([]);
  readonly includeDependencies = signal(true);
  readonly runtimeState = signal<Record<string, unknown>>({});
  readonly procedureLegendExpanded = signal(false);
  readonly highlightedProcedureCategory = signal<string | null>(null);

  private isResizing = false;
  private resizeListener: ((e: MouseEvent) => void) | null = null;
  private resizeEndListener: (() => void) | null = null;
  private pendingTimeouts: Array<ReturnType<typeof setTimeout>> = [];

  readonly nodeTypes: readonly NodeTypeConfig[] = [
    { type: 'tag', label: 'Topic', fill: '#e0e7ff', stroke: '#6366f1', legendColor: '#c7d2fe' },
    {
      type: 'representation',
      label: 'Representation',
      fill: '#f3f4f6',
      stroke: '#6b7280',
      legendColor: '#ffffff',
      legendBorderColor: '#9ca3af',
    },
  ];

  readonly procedureCategories = PROCEDURE_CATEGORIES.map((c) => ({
    category: c.name,
    label: c.name.charAt(0).toUpperCase() + c.name.slice(1),
    color: c.color,
  }));

  readonly hasSelectedTag = computed(() => this.selectedTags().length > 0);

  readonly representationsMap = computed(() => {
    const data = this.ontologyData();
    if (!data) {
      return new Map<string, Representation>();
    }
    return new Map(data.representations.map((r) => [r.id, r]));
  });

  readonly tagsMap = computed(() => {
    const data = this.ontologyData();
    if (!data) {
      return new Map<string, Tag>();
    }
    return new Map(data.tags.map((t) => [t.id, t]));
  });

  readonly rootRepresentationIds = computed(() => {
    const data = this.ontologyData();
    if (!data) {
      return new Set<string>();
    }
    const outputIds = new Set(data.procedures.map((p) => p.output_id));
    return new Set(data.representations.map((r) => r.id).filter((id) => !outputIds.has(id)));
  });

  readonly explorerNodes = computed(() => {
    const data = this.ontologyData();
    if (!data) {
      return [];
    }
    return this.computeExplorerNodes(data.tags);
  });

  readonly sourceProcedure = computed<Procedure | null>(() => {
    const procedures = this.sourceProcedures();
    return procedures.length > 0 ? procedures[0] : null;
  });

  readonly sourceProcedures = computed<Procedure[]>(() => {
    const node = this.selectedNode();
    const data = this.ontologyData();
    if (!node || !data || node['nodeType'] !== 'representation') {
      return [];
    }
    const repId = node['id'] as string;
    return data.procedures.filter((p) => p.output_id === repId);
  });

  ngAfterViewInit(): void {
    this.safeTimeout(() => this.initGraph(), 200);
  }

  initGraph(): void {
    if (!this.myDiagramDiv) {
      console.error('Ontology: myDiagramDiv is missing');
      return;
    }

    const div = this.myDiagramDiv.nativeElement;
    if (div.offsetWidth === 0 || div.offsetHeight === 0) {
      console.warn('Ontology: Diagram div has 0 dimensions, retrying in 100ms');
      this.safeTimeout(() => this.initGraph(), 100);
      return;
    }

    const $ = go.GraphObject.make;

    const currentDiagram = this.myDiagram();
    if (currentDiagram) {
      currentDiagram.div = null;
    }

    const diagram = $(go.Diagram, div, {
      'undoManager.isEnabled': true,
      'commandHandler.zoomFactor': 1.1,
      layout: $(go.LayeredDigraphLayout, {
        direction: 90,
        layerSpacing: 60,
        columnSpacing: 40,
        setsPortSpots: false,
        isOngoing: false,
        isRouting: true,
        packOption: go.LayeredDigraphLayout.PackStraighten,
        alignOption: go.LayeredDigraphLayout.AlignAll,
        aggressiveOption: go.LayeredDigraphLayout.AggressiveMore,
      }),
      initialAutoScale: go.Diagram.Uniform,
      'animationManager.isEnabled': true,
      scrollMode: go.Diagram.InfiniteScroll,
      allowHorizontalScroll: true,
      allowVerticalScroll: true,
      'toolManager.mouseWheelBehavior': go.ToolManager.WheelScroll,
      click: (e) => {
        if (e.diagram.selection.count === 0) {
          this.clearHighlights();
          this.highlightedLegendType.set(null);
          this.highlightedProcedureCategory.set(null);
          this.selectedNode.set(null);
          this.sidenavOpened.set(false);
        }
      },
      ChangedSelection: () => {
        this.onSelectionChanged();
      },
    });

    diagram.doMouseWheel = function () {
      const e = this.lastInput.event;
      if (e instanceof WheelEvent && e.deltaX !== 0) {
        this.lastInput.bubbles = false;
      }
      this.currentTool.doMouseWheel();
    };

    this.setupTemplates(diagram);
    this.myDiagram.set(diagram);

    this.destroyRef.onDestroy(() => {
      diagram.div = null;
      this.pendingTimeouts.forEach((id) => clearTimeout(id));
      this.pendingTimeouts = [];
      if (this.resizeListener) {
        document.removeEventListener('mousemove', this.resizeListener);
      }
      if (this.resizeEndListener) {
        document.removeEventListener('mouseup', this.resizeEndListener);
      }
    });
  }

  onSelectedTagsChange(tagIds: string[]): void {
    this.selectedTags.set(tagIds);
  }

  onIncludeDependenciesChanged(include: boolean): void {
    this.includeDependencies.set(include);
  }

  onLegendClick(type: string): void {
    const diagram = this.myDiagram();
    if (!diagram) {
      return;
    }

    if (this.highlightedLegendType() === type) {
      this.highlightedLegendType.set(null);
      diagram.clearSelection();
      diagram.clearHighlighteds();
      return;
    }

    this.highlightedLegendType.set(type);
    diagram.clearSelection();
    diagram.clearHighlighteds();
    diagram.startTransaction('highlight');

    const procedureNodeKeys = new Set<go.Key>();

    diagram.nodes.each((node) => {
      const nodeType = node.data?.nodeType;
      if (nodeType === type) {
        node.isHighlighted = true;
        if (type === 'procedure') {
          procedureNodeKeys.add(node.key);
        }
      } else {
        node.isHighlighted = false;
      }
    });

    diagram.links.each((link) => {
      link.isHighlighted = false;
    });

    if (type === 'procedure' && procedureNodeKeys.size > 0) {
      diagram.links.each((link) => {
        const fromNode = link.fromNode;
        const toNode = link.toNode;
        if ((fromNode && procedureNodeKeys.has(fromNode.key)) || (toNode && procedureNodeKeys.has(toNode.key))) {
          link.isHighlighted = true;
        }
      });
    }

    diagram.commitTransaction('highlight');
  }

  toggleProcedureLegend(): void {
    const expanded = !this.procedureLegendExpanded();
    this.procedureLegendExpanded.set(expanded);
    if (!expanded) {
      this.highlightedProcedureCategory.set(null);
      this.myDiagram()?.clearHighlighteds();
    }
  }

  onProcedureCategoryClick(category: string): void {
    const diagram = this.myDiagram();
    if (!diagram) {
      return;
    }

    if (this.highlightedProcedureCategory() === category) {
      this.highlightedProcedureCategory.set(null);
      this.highlightedLegendType.set(null);
      diagram.clearHighlighteds();
      return;
    }

    this.highlightedProcedureCategory.set(category);
    this.highlightedLegendType.set(null);
    diagram.clearHighlighteds();
    diagram.startTransaction('highlight-category');

    const procedureNodeKeys = new Set<go.Key>();

    diagram.nodes.each((node) => {
      const nodeType = node.data?.nodeType;
      const procCategory = node.data?.procedureCategory;
      if (nodeType === 'procedure' && procCategory === category) {
        node.isHighlighted = true;
        procedureNodeKeys.add(node.key);
      } else {
        node.isHighlighted = false;
      }
    });

    diagram.links.each((link) => {
      const fromNode = link.fromNode;
      const toNode = link.toNode;
      if ((fromNode && procedureNodeKeys.has(fromNode.key)) || (toNode && procedureNodeKeys.has(toNode.key))) {
        link.isHighlighted = true;
      } else {
        link.isHighlighted = false;
      }
    });

    diagram.commitTransaction('highlight-category');
  }

  closeSidenav(): void {
    this.sidenavOpened.set(false);
    this.selectedNode.set(null);
    this.myDiagram()?.clearSelection();
  }

  expandAllGroups(): void {
    this.myDiagram()?.commit((diag) => {
      diag.nodes.each((n) => {
        if (n instanceof go.Group) {
          n.isSubGraphExpanded = true;
        }
      });
    }, 'expand all');
  }

  collapseAllGroups(): void {
    this.myDiagram()?.commit((diag) => {
      diag.nodes.each((n) => {
        if (n instanceof go.Group) {
          n.isSubGraphExpanded = false;
        }
      });
    }, 'collapse all');
  }

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;

    const startX = event.clientX;
    const startWidth = this.detailsPanelWidth();
    const leftSidenavWidth = 200;
    const minGap = 50;

    this.resizeListener = (e: MouseEvent) => {
      if (!this.isResizing) {
        return;
      }
      const delta = startX - e.clientX;
      const availableWidth = window.innerWidth - leftSidenavWidth - minGap;
      const maxWidth = Math.min(availableWidth, window.innerWidth * 0.7);
      const newWidth = Math.max(300, Math.min(maxWidth, startWidth + delta));
      this.detailsPanelWidth.set(newWidth);
    };

    this.resizeEndListener = () => {
      this.isResizing = false;
      if (this.resizeListener) {
        document.removeEventListener('mousemove', this.resizeListener);
      }
      if (this.resizeEndListener) {
        document.removeEventListener('mouseup', this.resizeEndListener);
      }
    };

    document.addEventListener('mousemove', this.resizeListener);
    document.addEventListener('mouseup', this.resizeEndListener);
  }

  onNavigateToNode(nodeId: string): void {
    const diagram = this.myDiagram();
    if (!diagram) {
      return;
    }

    let node = diagram.findNodeForKey(nodeId);
    if (!node) {
      node = diagram.findNodeForKey(`${nodeId}_label`);
    }

    if (node) {
      diagram.select(node);

      const targetNode = node;
      requestAnimationFrame(() => {
        const anim = new go.Animation();
        anim.duration = 800;
        anim.easing = go.Animation.EaseOutExpo;

        const currentScale = diagram.scale;
        const targetScale = Math.max(currentScale, 1.0);

        const div = diagram.div;
        if (div) {
          const nodeCenter = targetNode.actualBounds.center;
          const panelWidth = this.sidenavOpened() ? this.detailsPanelWidth() : 0;
          const visibleWidth = div.offsetWidth - panelWidth;
          const scaledVisibleWidth = visibleWidth / targetScale;
          const scaledHeight = div.offsetHeight / targetScale;
          const targetPos = new go.Point(nodeCenter.x - scaledVisibleWidth / 2, nodeCenter.y - scaledHeight / 2);

          anim.add(diagram, 'position', diagram.position, targetPos);
          anim.add(diagram, 'scale', currentScale, targetScale);
          anim.start();
        } else {
          diagram.centerRect(targetNode.actualBounds);
        }
      });
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.sidenavOpened()) {
      this.closeSidenav();
    }
  }

  handleAutoExecuteRoot(): void {
    const data = this.ontologyData();
    if (!data) {
      console.warn('Ontology: No ontology data for auto-execution');
      return;
    }

    let rootId: string | undefined;
    const node = this.selectedNode();

    if (node && node['nodeType'] === 'representation') {
      const selectedReprId = node['id'] as string;
      rootId = this.findRootByTraversal(selectedReprId);
    }

    if (!rootId && node && node['nodeType'] === 'representation') {
      const selectedReprId = node['id'] as string;
      const selectedRepr = this.representationsMap().get(selectedReprId);

      if (selectedRepr) {
        const selectedTagSet = new Set(selectedRepr.tag_ids);
        for (const rid of this.rootRepresentationIds()) {
          const rootRepr = this.representationsMap().get(rid);
          if (rootRepr && rootRepr.tag_ids.some((t) => selectedTagSet.has(t))) {
            rootId = rid;
            break;
          }
        }
      }
    }

    if (!rootId) {
      rootId = Array.from(this.rootRepresentationIds())[0];
    }

    if (!rootId) {
      console.warn('Ontology: No root node found for auto-execution');
      return;
    }

    const rootNode = data.representations.find((r) => r.id === rootId);
    if (rootNode && rootNode.example_data) {
      this.executeRoot(rootId, rootNode.example_data);
    } else {
      console.warn('Ontology: Root node has no example data', rootId);
    }
  }

  executeRoot(rootId: string, values: unknown): void {
    const data = this.ontologyData();
    if (!data) {
      return;
    }

    const newRuntimeState = { ...this.runtimeState(), [rootId]: values };
    const queue = [rootId];
    const processed = new Set<string>();

    const executionPath: Array<{
      inputIds: string[];
      procedureId: string;
      outputId: string;
      category: ProcedureCategory;
    }> = [];

    let iterations = 0;
    while (queue.length > 0 && iterations < 100) {
      iterations++;
      const currentId = queue.shift()!;
      if (processed.has(currentId)) {
        continue;
      }
      processed.add(currentId);

      const procedures = data.procedures.filter((p) => p.input_ids.includes(currentId as `rep.${string}`));

      procedures.forEach((proc) => {
        if (proc.category === 'operation') {
          return;
        }

        const inputsReady = proc.input_ids.every((id) => newRuntimeState[id] !== undefined);

        if (inputsReady) {
          try {
            const inputValues = proc.input_ids.map((id) => newRuntimeState[id]);
            // eslint-disable-next-line @typescript-eslint/no-implied-eval
            const executor = new Function(`return ${proc.implementation}`)();
            const result = executor(...inputValues);
            newRuntimeState[proc.output_id] = result;
            queue.push(proc.output_id);

            executionPath.push({
              inputIds: [...proc.input_ids],
              procedureId: proc.id,
              outputId: proc.output_id,
              category: proc.category,
            });
          } catch (e) {
            console.error(`Error executing procedure ${proc.id}:`, e);
          }
        }
      });
    }

    this.runtimeState.set(newRuntimeState);
    this.animateExecutionFlow(executionPath);
  }

  private loadOntology(): void {
    this.ontologyData.set(masterOntology);
  }

  private safeTimeout(callback: () => void, delay: number): void {
    const id = setTimeout(() => {
      this.pendingTimeouts = this.pendingTimeouts.filter((t) => t !== id);
      callback();
    }, delay);
    this.pendingTimeouts.push(id);
  }

  private setupTemplates(diagram: go.Diagram): void {
    const $ = go.GraphObject.make;

    diagram.groupTemplate = $(
      go.Group,
      'Auto',
      {
        layout: $(go.LayeredDigraphLayout, {
          direction: 90,
          columnSpacing: 30,
          layerSpacing: 45,
          packOption: go.LayeredDigraphLayout.PackStraighten,
          alignOption: go.LayeredDigraphLayout.AlignAll,
          aggressiveOption: go.LayeredDigraphLayout.AggressiveMore,
          isRouting: true,
        }),
        isSubGraphExpanded: true,
        selectionAdorned: false,
        click: (_e, obj) => this.onNodeClick(obj as go.Node),
        subGraphExpandedChanged: (group: go.Group) => {
          const btn = group.findObject('CollapseButton') as go.Shape;
          if (btn) {
            btn.figure = group.isSubGraphExpanded ? 'MinusLine' : 'PlusLine';
          }
        },
      },
      $(
        go.Shape,
        'RoundedRectangle',
        {
          fill: '#f9fafb',
          stroke: '#e5e7eb',
          strokeWidth: 2,
        },
        new go.Binding('stroke', 'isSelected', (sel) => (sel ? '#6366f1' : '#e5e7eb')).ofObject(),
        new go.Binding('stroke', 'isHighlighted', (hl) => (hl ? '#6366f1' : '#e5e7eb')).ofObject(),
      ),
      $(
        go.Panel,
        'Vertical',
        { margin: 8 },
        $(
          go.Panel,
          'Horizontal',
          { alignment: go.Spot.Left, margin: new go.Margin(0, 0, 4, 0) },
          $('SubGraphExpanderButton', {
            name: 'CollapseButton',
            margin: new go.Margin(0, 6, 0, 0),
            'ButtonBorder.fill': '#ffffff',
            'ButtonBorder.stroke': '#e5e7eb',
            'ButtonBorder.strokeWidth': 1,
            _buttonFillOver: '#f3f4f6',
            _buttonStrokeOver: '#9ca3af',
            'ButtonIcon.stroke': '#6b7280',
            'ButtonIcon.strokeWidth': 1.5,
          }),
          $(
            go.TextBlock,
            {
              font: '500 11px sans-serif',
              stroke: '#6b7280',
            },
            new go.Binding('text', 'label'),
          ),
        ),
        $(go.Placeholder, { padding: 2 }),
      ),
    );

    diagram.nodeTemplate = $(
      go.Node,
      'Auto',
      {
        click: (_e, obj) => this.onNodeClick(obj as go.Node),
        fromSpot: go.Spot.Bottom,
        toSpot: go.Spot.Top,
        shadowVisible: true,
        shadowColor: 'rgba(0,0,0,0.05)',
        shadowBlur: 4,
        shadowOffset: new go.Point(0, 2),
        selectionAdorned: false,
      },
      $(
        go.Shape,
        'RoundedRectangle',
        { strokeWidth: 2, stroke: '#e5e7eb', fill: '#ffffff' },
        new go.Binding('stroke', 'isSelected', (sel) => (sel ? '#4b5563' : '#e5e7eb')).ofObject(),
        new go.Binding('stroke', 'isHighlighted', (hl) => (hl ? '#4b5563' : '#e5e7eb')).ofObject(),
      ),
      $(
        go.Panel,
        'Horizontal',
        { margin: 4, alignment: go.Spot.Center, defaultAlignment: go.Spot.Center },
        $(
          go.Shape,
          {
            width: 14,
            height: 14,
            margin: new go.Margin(0, 5, 0, 0),
            strokeWidth: 1.5,
            stroke: '#6b7280',
            fill: 'transparent',
            geometryStretch: go.GraphObject.Uniform,
          },
          new go.Binding('geometryString', 'representationCategory', (cat: string) =>
            this.getRepresentationCategoryGeometry(cat),
          ),
        ),
        $(go.TextBlock, { font: '12px sans-serif', stroke: '#374151' }, new go.Binding('text', 'label')),
      ),
    );

    diagram.nodeTemplateMap.add(
      'ProcedureLabel',
      $(
        go.Node,
        'Auto',
        {
          click: (_e, obj) => this.onNodeClick(obj as go.Node),
          fromSpot: go.Spot.Bottom,
          toSpot: go.Spot.Top,
          cursor: 'pointer',
          layerName: 'Foreground',
          background: 'transparent',
          selectionAdorned: false,
        },
        $(
          go.Shape,
          'RoundedRectangle',
          {
            parameter1: 4,
            strokeWidth: 2,
          },
          new go.Binding('fill', 'procedureCategory', (cat) => this.getProcedureCategoryColors(cat).fill),
          new go.Binding('stroke', 'procedureCategory', (cat) => this.getProcedureCategoryColors(cat).stroke),
          new go.Binding('stroke', 'isSelected', (sel, obj) => {
            const cat = obj.part?.data?.procedureCategory;
            const colors = this.getProcedureCategoryColors(cat);
            return sel ? colors.highlight : colors.stroke;
          }).ofObject(),
          new go.Binding('stroke', 'isHighlighted', (hl, obj) => {
            const cat = obj.part?.data?.procedureCategory;
            const colors = this.getProcedureCategoryColors(cat);
            return hl ? colors.highlight : colors.stroke;
          }).ofObject(),
        ),
        $(
          go.TextBlock,
          { margin: new go.Margin(2, 4), font: '10px sans-serif' },
          new go.Binding('stroke', 'procedureCategory', (cat) => this.getProcedureCategoryColors(cat).text),
          new go.Binding('text', 'label'),
        ),
      ),
    );

    diagram.linkTemplate = $(
      go.Link,
      {
        routing: go.Link.Orthogonal,
        curve: go.Link.JumpGap,
        corner: 8,
        selectable: false,
      },
      $(
        go.Shape,
        { strokeWidth: 1.5, stroke: '#c4b5fd' },
        new go.Binding('stroke', 'isHighlighted', (hl) => (hl ? '#8b5cf6' : '#c4b5fd')).ofObject(),
      ),
      $(
        go.Shape,
        { toArrow: 'Standard', fill: '#c4b5fd', stroke: '#c4b5fd', strokeWidth: 1, scale: 0.8 },
        new go.Binding('fill', 'isHighlighted', (hl) => (hl ? '#8b5cf6' : '#c4b5fd')).ofObject(),
        new go.Binding('stroke', 'isHighlighted', (hl) => (hl ? '#8b5cf6' : '#c4b5fd')).ofObject(),
      ),
    );

    diagram.linkTemplateMap.add(
      'Dependency',
      $(
        go.Link,
        {
          routing: go.Link.Orthogonal,
          curve: go.Link.JumpGap,
          corner: 8,
          selectable: false,
        },
        $(go.Shape, { strokeWidth: 1.5, stroke: '#94a3b8', strokeDashArray: [4, 4] }),
        $(go.Shape, { toArrow: 'OpenTriangle', fill: null, stroke: '#94a3b8', strokeWidth: 1.5 }),
      ),
    );

    diagram.linkTemplateMap.add(
      'ProcedureInputLink',
      $(
        go.Link,
        {
          routing: go.Link.Orthogonal,
          curve: go.Link.JumpGap,
          corner: 8,
          selectable: false,
        },
        $(
          go.Shape,
          { strokeWidth: 1.5 },
          new go.Binding('stroke', 'procedureCategory', (cat) => this.getProcedureCategoryColors(cat).edge),
          new go.Binding('stroke', 'isHighlighted', (hl, obj) => {
            const cat = obj.part?.data?.procedureCategory;
            const colors = this.getProcedureCategoryColors(cat);
            return hl ? colors.highlight : colors.edge;
          }).ofObject(),
        ),
      ),
    );

    diagram.linkTemplateMap.add(
      'ProcedureOutputLink',
      $(
        go.Link,
        {
          routing: go.Link.Orthogonal,
          curve: go.Link.JumpGap,
          corner: 8,
          selectable: false,
        },
        $(
          go.Shape,
          { strokeWidth: 1.5 },
          new go.Binding('stroke', 'procedureCategory', (cat) => this.getProcedureCategoryColors(cat).edge),
          new go.Binding('stroke', 'isHighlighted', (hl, obj) => {
            const cat = obj.part?.data?.procedureCategory;
            const colors = this.getProcedureCategoryColors(cat);
            return hl ? colors.highlight : colors.edge;
          }).ofObject(),
        ),
        $(
          go.Shape,
          { toArrow: 'Standard', strokeWidth: 0 },
          new go.Binding('fill', 'procedureCategory', (cat) => this.getProcedureCategoryColors(cat).edge),
          new go.Binding('fill', 'isHighlighted', (hl, obj) => {
            const cat = obj.part?.data?.procedureCategory;
            const colors = this.getProcedureCategoryColors(cat);
            return hl ? colors.highlight : colors.edge;
          }).ofObject(),
          new go.Binding('stroke', 'procedureCategory', (cat) => this.getProcedureCategoryColors(cat).edge),
        ),
      ),
    );
  }

  private renderGraphInternal(diagram: go.Diagram, data: MasterOntology, hasTag: boolean, includeDeps: boolean): void {
    if (!hasTag) {
      diagram.model = new go.GraphLinksModel([], []);
      return;
    }

    const { tags, representations, procedures } = data;
    const selectedTagSet = this.getExpandedSelectedTags(tags);
    const nodeDataArray: Array<Record<string, unknown>> = [];
    const linkDataArray: Array<Record<string, unknown>> = [];

    const tagsToShow = new Set<string>();
    const tagsMap = new Map<string, Tag>();
    tags.forEach((t) => tagsMap.set(t.id, t));

    selectedTagSet.forEach((tagId) => {
      let currentId: string | undefined = tagId;
      while (currentId && tagsMap.has(currentId)) {
        tagsToShow.add(currentId);
        currentId = tagsMap.get(currentId)?.parent_id;
      }
    });

    tags.forEach((t) => {
      if (tagsToShow.has(t.id)) {
        nodeDataArray.push({
          ...t,
          key: t.id,
          isGroup: true,
          label: t.name,
          nodeType: 'tag',
          group: t.parent_id && tagsToShow.has(t.parent_id) ? t.parent_id : undefined,
        });
      }
    });

    const initialReprs = representations.filter((r) => r.tag_ids.some((tid) => selectedTagSet.has(tid)));
    const relevantReprIds = new Set(initialReprs.map((r) => r.id));

    procedures.forEach((p) => {
      const procedureTagIds = p.tag_ids ?? [];
      if (procedureTagIds.some((tid) => selectedTagSet.has(tid))) {
        relevantReprIds.add(p.output_id);
        p.input_ids.forEach((id) => relevantReprIds.add(id));
      }
    });

    procedures.forEach((p) => {
      const isOutputRelevant = relevantReprIds.has(p.output_id);
      const isInputRelevant = p.input_ids.some((id) => relevantReprIds.has(id));

      if (isOutputRelevant || isInputRelevant) {
        relevantReprIds.add(p.output_id);
        p.input_ids.forEach((id) => relevantReprIds.add(id));
      }
    });

    const relevantReprs = representations.filter((r) => relevantReprIds.has(r.id));

    relevantReprs.forEach((r) => {
      nodeDataArray.push({
        key: r.id,
        label: r.name,
        ...r,
        representationCategory: r.category,
        nodeType: 'representation',
        color: '#fed7aa',
        group: r.tag_ids.find((tid) => tagsToShow.has(tid)),
      });
    });

    const relevantProcs = procedures.filter((p) => {
      const inputRelevant = p.input_ids.some((id) => relevantReprIds.has(id));
      const outputRelevant = relevantReprIds.has(p.output_id);
      return inputRelevant || outputRelevant;
    });
    const relevantProcIds = new Set(relevantProcs.map((p) => p.id));

    const reprToTags = new Map<string, string[]>();
    representations.forEach((r) => reprToTags.set(r.id, r.tag_ids));

    relevantProcs.forEach((p) => {
      const labelKey = `${p.id}_label`;
      const outputTags = reprToTags.get(p.output_id) ?? [];
      const procedureTags = p.tag_ids ?? [];
      const inferredGroup =
        procedureTags.find((tid) => tagsToShow.has(tid)) ?? outputTags.find((tid) => tagsToShow.has(tid));

      nodeDataArray.push({
        key: labelKey,
        label: p.name,
        group: inferredGroup,
        ...p,
        procedureCategory: p.category,
        category: 'ProcedureLabel',
        nodeType: 'procedure',
      });

      if (p.input_ids && p.input_ids.length > 0) {
        const outputId = p.output_id;

        p.input_ids.forEach((inputId) => {
          if (relevantReprIds.has(inputId)) {
            linkDataArray.push({
              from: inputId,
              to: labelKey,
              category: 'ProcedureInputLink',
              procedureCategory: p.category,
            });
          }
        });

        if (relevantReprIds.has(outputId)) {
          linkDataArray.push({
            from: labelKey,
            to: outputId,
            category: 'ProcedureOutputLink',
            procedureCategory: p.category,
          });
        }
      }
    });

    if (includeDeps) {
      relevantProcs.forEach((p) => {
        if (p.dependencies) {
          p.dependencies.forEach((depId: string) => {
            const depProc = procedures.find((proc) => proc.id === depId);
            if (depProc && relevantProcIds.has(depId as `proc.${string}`) && relevantReprIds.has(depProc.output_id)) {
              linkDataArray.push({
                from: depProc.output_id,
                to: `${p.id}_label`,
                category: 'Dependency',
              });
            }
          });
        }
      });
    }

    if (nodeDataArray.length === 0 && hasTag) {
      console.warn('Ontology: nodeDataArray is empty despite hasSelectedTag=true');
    }

    diagram.model = new go.GraphLinksModel({
      nodeDataArray,
      linkDataArray,
    });
  }

  private getExpandedSelectedTags(tags: Tag[]): Set<string> {
    const tagChildrenMap = new Map<string, string[]>();
    tags.forEach((t) => {
      if (t.parent_id) {
        const children = tagChildrenMap.get(t.parent_id) || [];
        children.push(t.id);
        tagChildrenMap.set(t.parent_id, children);
      }
    });

    const getDescendants = (tagId: string): string[] => {
      const children = tagChildrenMap.get(tagId) || [];
      return children.reduce((acc, childId) => {
        return [...acc, childId, ...getDescendants(childId)];
      }, [] as string[]);
    };

    const expandedSelection = new Set(this.selectedTags());
    this.selectedTags().forEach((tagId) => {
      const descendants = getDescendants(tagId);
      descendants.forEach((d) => expandedSelection.add(d));
    });

    return expandedSelection;
  }

  private findRootByTraversal(representationId: string): string | undefined {
    const data = this.ontologyData();
    if (!data) {
      return undefined;
    }

    const rootIds = this.rootRepresentationIds();
    if (rootIds.has(representationId)) {
      return representationId;
    }

    const visited = new Set<string>();
    const queue = [representationId];

    while (queue.length > 0) {
      const currentId = queue.shift()!;

      if (visited.has(currentId)) {
        continue;
      }
      visited.add(currentId);

      if (rootIds.has(currentId)) {
        return currentId;
      }

      const sourceProcedures = data.procedures.filter((p) => p.output_id === currentId && p.category !== 'operation');

      for (const proc of sourceProcedures) {
        for (const inputId of proc.input_ids) {
          if (!visited.has(inputId)) {
            queue.push(inputId);
          }
        }
      }
    }

    return undefined;
  }

  private getProcedureCategoryColors(category: string | undefined): ProcedureCategoryColors {
    return PROCEDURE_CATEGORY_COLORS[category ?? 'transformation'] ?? PROCEDURE_CATEGORY_COLORS['transformation'];
  }

  private animateExecutionFlow(
    executionPath: Array<{ inputIds: string[]; procedureId: string; outputId: string; category: ProcedureCategory }>,
  ): void {
    const diagram = this.myDiagram();
    if (!diagram || executionPath.length === 0) {
      return;
    }

    const ANIMATION_DURATION = 500;

    const tier1Links: Array<{ link: go.Link; color: string }> = [];
    const tier2Links: Array<{ link: go.Link; color: string }> = [];

    executionPath.forEach((step) => {
      const labelKey = `${step.procedureId}_label`;
      const colors = this.getProcedureCategoryColors(step.category);
      const animationColor = colors.highlight;

      step.inputIds.forEach((inputId) => {
        const inputLink = this.findLinkBetween(inputId, labelKey);
        if (inputLink) {
          tier1Links.push({ link: inputLink, color: animationColor });
        }
      });

      const outputLink = this.findLinkBetween(labelKey, step.outputId);
      if (outputLink) {
        tier2Links.push({ link: outputLink, color: animationColor });
      }
    });

    const allLinks = [...tier1Links.map((l) => l.link), ...tier2Links.map((l) => l.link)];

    if (tier1Links.length > 0) {
      tier1Links.forEach(({ link, color }) => {
        this.animateLinkDrawing(link, color, ANIMATION_DURATION);
      });

      this.safeTimeout(() => {
        tier2Links.forEach(({ link, color }) => {
          this.animateLinkDrawing(link, color, ANIMATION_DURATION);
        });
      }, ANIMATION_DURATION * 0.5);

      this.safeTimeout(
        () => {
          this.fadeOutLinkAnimations(allLinks);
        },
        ANIMATION_DURATION * 1.5 + 800,
      );
    } else if (tier2Links.length > 0) {
      tier2Links.forEach(({ link, color }) => {
        this.animateLinkDrawing(link, color, ANIMATION_DURATION);
      });
      this.safeTimeout(() => {
        this.fadeOutLinkAnimations(allLinks);
      }, ANIMATION_DURATION + 1500);
    }
  }

  private findLinkBetween(fromKey: string, toKey: string): go.Link | null {
    const diagram = this.myDiagram();
    if (!diagram) {
      return null;
    }

    let foundLink: go.Link | null = null;
    diagram.links.each((link) => {
      if (link.data?.from === fromKey && link.data?.to === toKey) {
        foundLink = link;
      }
    });
    return foundLink;
  }

  private animateLinkDrawing(link: go.Link, color: string, duration: number, onComplete?: () => void): void {
    const diagram = this.myDiagram();
    if (!diagram) {
      onComplete?.();
      return;
    }

    const mainShape = link.elt(0) as go.Shape | null;

    if (!mainShape) {
      onComplete?.();
      return;
    }

    const pathLength = this.estimateLinkLength(link);

    let arrowShape: go.Shape | null = null;
    if (link.elements.count > 1) {
      arrowShape = link.elt(1) as go.Shape | null;
    }

    diagram.startTransaction('animateLink');

    mainShape.stroke = color;
    mainShape.strokeDashArray = [pathLength, pathLength];
    mainShape.strokeDashOffset = pathLength;

    diagram.commitTransaction('animateLink');

    const animation = new go.Animation();
    animation.duration = duration;
    animation.easing = go.Animation.EaseLinear;
    animation.add(mainShape, 'strokeDashOffset', pathLength, 0);

    if (arrowShape) {
      animation.add(arrowShape, 'fill', arrowShape.fill, color);
      animation.add(arrowShape, 'stroke', arrowShape.stroke, color);
    }

    animation.finished = () => {
      const d = this.myDiagram();
      if (d) {
        d.startTransaction('finishAnim');
        mainShape.strokeDashArray = null;
        mainShape.strokeDashOffset = 0;
        d.commitTransaction('finishAnim');
      }
      onComplete?.();
    };

    animation.start();
  }

  private estimateLinkLength(link: go.Link): number {
    if (!link.geometry) {
      const fromNode = link.fromNode;
      const toNode = link.toNode;
      if (fromNode && toNode) {
        const dx = toNode.location.x - fromNode.location.x;
        const dy = toNode.location.y - fromNode.location.y;
        return Math.sqrt(dx * dx + dy * dy) + 50;
      }
      return 500;
    }

    const bounds = link.geometry.bounds;
    return bounds.width + bounds.height + 100;
  }

  private fadeOutLinkAnimations(links: go.Link[]): void {
    const diagram = this.myDiagram();
    if (!diagram) {
      return;
    }

    const FADE_DURATION = 800;

    const animation = new go.Animation();
    animation.duration = FADE_DURATION;
    animation.easing = go.Animation.EaseOutQuad;

    links.forEach((link) => {
      const procedureCategory = link.data?.procedureCategory as ProcedureCategory | undefined;
      const colors = this.getProcedureCategoryColors(procedureCategory);
      const targetStroke = colors.edge;

      const mainShape = link.elt(0) as go.Shape | null;
      if (mainShape) {
        animation.add(mainShape, 'stroke', mainShape.stroke, targetStroke);
      }

      if (link.elements.count > 1) {
        const arrowShape = link.elt(1) as go.Shape | null;
        if (arrowShape) {
          animation.add(arrowShape, 'fill', arrowShape.fill, targetStroke);
          animation.add(arrowShape, 'stroke', arrowShape.stroke, targetStroke);
        }
      }
    });

    animation.start();
  }

  private computeExplorerNodes(tags: Tag[]): Array<TreeNode<Tag, VerificationIndicator>> {
    const tagMap = new Map<string, Tag>();
    tags.forEach((t) => tagMap.set(t.id, t));

    const childrenMap = new Map<string, string[]>();
    tags.forEach((t) => {
      if (t.parent_id) {
        const children = childrenMap.get(t.parent_id) ?? [];
        children.push(t.id);
        childrenMap.set(t.parent_id, children);
      }
    });

    const getPath = (tag: Tag): string[] => {
      const path: string[] = [tag.name];
      let current = tag;
      while (current.parent_id && tagMap.has(current.parent_id)) {
        current = tagMap.get(current.parent_id)!;
        path.unshift(current.name);
      }
      return path;
    };

    const getDirectStatus = (tag: Tag): VerificationIndicator | null => {
      if (!tag.verification || tag.verification.length === 0) {
        return null;
      }
      if (tag.verification.length >= 2) {
        return 'verified';
      }
      return 'partial';
    };

    const computeAggregatedStatus = (tagId: string): VerificationIndicator => {
      const tag = tagMap.get(tagId);
      if (!tag) {
        return 'none';
      }

      const directStatus = getDirectStatus(tag);
      if (directStatus) {
        return directStatus;
      }

      const childIds = childrenMap.get(tagId);
      if (!childIds || childIds.length === 0) {
        return 'none';
      }

      const childStatuses = childIds.map((childId) => computeAggregatedStatus(childId));

      const allVerified = childStatuses.every((s) => s === 'verified');
      if (allVerified) {
        return 'verified';
      }

      const hasAnyProgress = childStatuses.some((s) => s === 'verified' || s === 'partial');
      if (hasAnyProgress) {
        return 'partial';
      }

      return 'none';
    };

    return tags.map((t) => ({
      id: t.id,
      name: t.name,
      path: getPath(t),
      originalData: t,
      metadata: computeAggregatedStatus(t.id),
    }));
  }

  private onNodeClick(_node: go.Node): void {
    // Selection handling is done via ChangedSelection listener (onSelectionChanged)
  }

  private onSelectionChanged(): void {
    const diagram = this.myDiagram();
    if (!diagram) {
      return;
    }

    const selection = diagram.selection;
    if (selection.count === 0) {
      return;
    }

    const selectedNode = selection.first();
    if (!(selectedNode instanceof go.Node)) {
      return;
    }

    this.selectedNode.set(selectedNode.data);
    this.sidenavOpened.set(true);
    this.highlightedLegendType.set(null);

    diagram.startTransaction('highlight');
    this.clearHighlights();

    const node = this.selectedNode();
    if (node && node['nodeType'] === 'procedure') {
      this.highlightLinksForNode(selectedNode);
    }

    diagram.commitTransaction('highlight');
  }

  private clearHighlights(): void {
    const diagram = this.myDiagram();
    if (!diagram) {
      return;
    }
    diagram.nodes.each((n) => {
      n.isHighlighted = false;
    });
    diagram.links.each((l) => {
      l.isHighlighted = false;
    });
  }

  private highlightLinksForNode(node: go.Node): void {
    const diagram = this.myDiagram();
    if (!diagram) {
      return;
    }
    node.linksConnected.each((link) => {
      link.isHighlighted = true;
    });
  }

  private getRepresentationCategoryGeometry(category: string): string {
    return REPRESENTATION_CATEGORY_GEOMETRIES[category] ?? REPRESENTATION_CATEGORY_GEOMETRIES['default'];
  }
}
